import { Droplets, TriangleAlert } from "lucide-react";
import { useState, type FormEvent } from "react";

import { MAX_REFILL_LITERS, POOL_ACTIONS } from "../../shared/pool-actions";
import { rangeWarning } from "../../shared/pool-thresholds";
import type { RouterOutputs } from "../lib/trpc";
import { isOfflineQueued } from "../lib/offline-link";
import { trpc } from "../lib/trpc";

type Pool = RouterOutputs["pools"]["list"][number];

/** Il numero scritto a mano: virgola o punto, e il campo vuoto vale "non misurato". */
export function leggiMisura(value: string): number | null {
  const pulito = value.trim().replace(",", ".");
  if (!pulito) return null;
  const numero = Number(pulito);
  return Number.isFinite(numero) ? numero : null;
}

/**
 * Rilevazione rapida di una piscina.
 *
 * Vive in due posti, la pagina Piscine e la scheda di un intervento, ed e lo stesso modulo:
 * chi e sul campo apre il lavoro del giorno e scrive li i valori, senza cercare un'altra schermata.
 *
 * Un valore fuori soglia **avvisa e basta**: il pulsante non si spegne mai e il salvataggio
 * passa. L'app non ha l'acqua davanti, chi misura si.
 *
 * Le spunte sono le azioni del foglio di carta che il cliente compila ogni mattina, e il
 * reintegro porta con se i litri: senza numero, il consumo d'acqua del condominio non si sa.
 */
export function PoolReadingForm({
  pools,
  interventionId = null,
  onSaved,
}: {
  pools: Pool[];
  interventionId?: string | null;
  onSaved?: () => void;
}) {
  const [poolId, setPoolId] = useState(pools[0]?.id ?? "");
  const [avvisi, setAvvisi] = useState<string[]>([]);
  const [esito, setEsito] = useState<string | null>(null);
  const record = trpc.pools.record.useMutation();
  const piscina = pools.find((item) => item.id === poolId) ?? pools[0];

  function ricalcolaAvvisi(cloro: number | null, ph: number | null) {
    if (!piscina) return [];
    return [
      rangeWarning("Il cloro", cloro, piscina.chlorine, " mg/l"),
      rangeWarning("Il pH", ph, piscina.ph),
    ].filter((avviso): avviso is string => Boolean(avviso));
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    // I valori si rileggono dal modulo con FormData, che guarda i campi veri e non lo stato
    // React: dettatura e incolla scrivono nel campo senza emettere l'evento `input`.
    const cloro = leggiMisura(String(data.get("chlorine") ?? ""));
    const ph = leggiMisura(String(data.get("ph") ?? ""));
    if (cloro == null && ph == null) {
      setEsito("Scrivi almeno il cloro oppure il pH.");
      return;
    }
    setAvvisi(ricalcolaAvvisi(cloro, ph));
    const litri = leggiMisura(String(data.get("waterRefillLiters") ?? ""));
    if (litri != null && (litri < 0 || litri > MAX_REFILL_LITERS)) {
      setEsito(`I litri di reintegro stanno fra 0 e ${MAX_REFILL_LITERS}.`);
      return;
    }
    try {
      const salvata = await record.mutateAsync({
        poolId: piscina?.id ?? poolId,
        interventionId,
        chlorine: cloro,
        ph,
        products: String(data.get("products") ?? "").trim() || null,
        filterCleaning: data.get("filterCleaning") === "on",
        skimmerCleaning: data.get("skimmerCleaning") === "on",
        chlorineLoad: data.get("chlorineLoad") === "on",
        poolCleaning: data.get("poolCleaning") === "on",
        waterRefill: data.get("waterRefill") === "on",
        waterRefillLiters: litri,
        notes: String(data.get("notes") ?? "").trim() || null,
      });
      form.reset();
      if (isOfflineQueued(salvata)) {
        setEsito("Rilevazione salvata sul dispositivo. Parte da sola appena torna la rete.");
        onSaved?.();
        return;
      }
      setEsito(salvata?.outOfRange
        ? "Rilevazione salvata. I valori fuori soglia restano segnalati nello storico."
        : "Rilevazione salvata.");
      onSaved?.();
    } catch (error) {
      setEsito(error instanceof Error ? error.message : "Registrazione non riuscita.");
    }
  }

  if (pools.length === 0) return null;

  return (
    <form className="pool-reading" onSubmit={submit}>
      <div className="section-heading">
        <div><p className="eyebrow">Registro piscine</p><h2>Rilevazione rapida</h2></div>
        <span className="metric"><Droplets size={18} /> {pools.length}</span>
      </div>
      <div className="form-grid">
        {pools.length > 1 && (
          <label className="form-grid__wide">
            <span>Piscina</span>
            <select name="poolId" value={poolId} onChange={(event) => setPoolId(event.target.value)}>
              {pools.map((item) => <option key={item.id} value={item.id}>{item.siteName} - {item.name}</option>)}
            </select>
          </label>
        )}
        <label>
          <span>Cloro in mg/l</span>
          <input
            name="chlorine"
            inputMode="decimal"
            placeholder={piscina ? `da ${piscina.chlorine.min} a ${piscina.chlorine.max}` : ""}
            onInput={(event) => setAvvisi(ricalcolaAvvisi(leggiMisura(event.currentTarget.value), null))}
          />
        </label>
        <label>
          <span>pH</span>
          <input
            name="ph"
            inputMode="decimal"
            placeholder={piscina ? `da ${piscina.ph.min} a ${piscina.ph.max}` : ""}
            onInput={(event) => setAvvisi(ricalcolaAvvisi(null, leggiMisura(event.currentTarget.value)))}
          />
        </label>
        <label><span>Prodotti aggiunti</span><input name="products" placeholder="2 kg di cloro granulare" /></label>
        <fieldset className="form-grid__wide assignee-field">
          <legend>Azioni fatte</legend>
          <div>
            {POOL_ACTIONS.map((azione) => (
              <label className="check-control" key={azione.key}>
                <input type="checkbox" name={azione.key} /> {azione.label}
              </label>
            ))}
          </div>
        </fieldset>
        <label>
          <span>Litri di reintegro</span>
          <input name="waterRefillLiters" inputMode="decimal" placeholder="quanti litri, se misurati" />
        </label>
        <label className="form-grid__wide"><span>Note</span><input name="notes" placeholder="Acqua torbida, filtro pulito" /></label>
      </div>
      {avvisi.map((avviso) => (
        <p className="pool-warning" role="status" key={avviso}><TriangleAlert size={17} aria-hidden="true" /> {avviso}</p>
      ))}
      {esito && <p className="notice" role="status">{esito}</p>}
      <div className="form-actions">
        <button className={`button button--primary${record.isPending ? " button--saving" : ""}`}>
          {record.isPending ? "Salvataggio..." : "Registra la rilevazione"}
        </button>
      </div>
    </form>
  );
}
